import { useEffect, useState } from 'react'
import { CheckCircleIcon, ExclamationTriangleIcon, ClockIcon } from '@heroicons/react/24/outline'

export default function OAuthCallback() {
  const [status, setStatus] = useState('processing') // processing, success, error
  const [message, setMessage] = useState('Connecting your calendar...')

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search)
        const code = urlParams.get('code')
        const state = urlParams.get('state')
        const error = urlParams.get('error')

        // Check for OAuth errors
        if (error) {
          throw new Error(`OAuth error: ${error}`)
        }

        if (!code || !state) {
          throw new Error('Missing authorization code or state parameter')
        }

        setMessage('Processing authorization...')

        // Send to backend for token exchange
        const response = await fetch('/api/calendar/oauth-callback', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code: code,
            state: state
          })
        })

        const data = await response.json()

        if (!response.ok) {
          throw new Error(data.error || 'Failed to connect calendar')
        }

        if (data.success) {
          setStatus('success')
          setMessage(`Calendar connected successfully! Connected to ${data.calendar_info?.email || 'your calendar'}.`)
          
          // Notify parent window if this is a popup
          if (window.opener) {
            window.opener.postMessage({
              type: 'CALENDAR_CONNECTED',
              data: data.calendar_info
            }, window.location.origin)
            
            // Close popup after a short delay
            setTimeout(() => {
              window.close()
            }, 2000)
          } else {
            // If not a popup, redirect to settings after delay
            setTimeout(() => {
              window.location.href = '/settings?tab=calendar'
            }, 3000)
          }
        } else {
          throw new Error(data.error || 'Unknown error occurred')
        }

      } catch (error) {
        console.error('OAuth callback error:', error)
        setStatus('error')
        setMessage(error.message || 'Failed to connect calendar')
        
        // If this is a popup, we should still try to close it
        if (window.opener) {
          setTimeout(() => {
            window.close()
          }, 5000)
        }
      }
    }

    handleOAuthCallback()
  }, [])

  const getIcon = () => {
    switch (status) {
      case 'processing':
        return <ClockIcon className="h-16 w-16 text-blue-500 animate-spin" />
      case 'success':
        return <CheckCircleIcon className="h-16 w-16 text-green-500" />
      case 'error':
        return <ExclamationTriangleIcon className="h-16 w-16 text-red-500" />
      default:
        return <ClockIcon className="h-16 w-16 text-blue-500" />
    }
  }

  const getStatusColor = () => {
    switch (status) {
      case 'processing':
        return 'text-blue-600'
      case 'success':
        return 'text-green-600'
      case 'error':
        return 'text-red-600'
      default:
        return 'text-blue-600'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="flex justify-center mb-6">
          {getIcon()}
        </div>
        
        <h1 className={`text-2xl font-bold mb-4 ${getStatusColor()}`}>
          {status === 'processing' && 'Connecting Calendar'}
          {status === 'success' && 'Calendar Connected!'}
          {status === 'error' && 'Connection Failed'}
        </h1>
        
        <p className="text-gray-600 mb-6">
          {message}
        </p>
        
        {status === 'processing' && (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}
        
        {status === 'success' && (
          <div className="text-sm text-green-600">
            {window.opener ? (
              <p>This window will close automatically...</p>
            ) : (
              <p>Redirecting to settings...</p>
            )}
          </div>
        )}
        
        {status === 'error' && (
          <div className="space-y-4">
            <button
              onClick={() => window.location.href = '/settings?tab=calendar'}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Return to Settings
            </button>
            
            {window.opener && (
              <button
                onClick={() => window.close()}
                className="block mx-auto px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Close Window
              </button>
            )}
          </div>
        )}
        
        <div className="mt-8 text-xs text-gray-400">
          <p>Secure OAuth connection powered by Nylas</p>
        </div>
      </div>
    </div>
  )
}